import { fmtDate, getTodayStr } from './formatters';

/**
 * A fila de SUGESTÕES de cobrança (fase 2, C1 + B2), contra o contrato do backend em docs/cobranca-fase-2-contrato.md
 * (#571 e o conserto dela). Prévia aprovada pelo Heitor em 30/10/2026: https://claude.ai/artifact/3r9uuPsfcfDr9vZRb5bDfQ
 * (S1 J1 O1).
 *
 * O servidor decide QUANDO sugerir e REAVALIA na aprovação. A tela só agrupa, mostra e manda o clique: nenhum motivo de
 * retenção é recalculado aqui, porque seria uma segunda cópia da régua.
 */

export type StatusDaSugestao = 'sugerida' | 'aprovada' | 'enviada' | 'falhou' | 'descartada' | 'obsoleta';

export interface FaturaDaSugestao {
  id: number;
  code: string;
  dueDate: string;
  amountDue: number;
  notasAutorizadas: number;
}

export interface SugestaoDeCobranca {
  id: number;
  kind: 'email' | 'oficio';
  track: 'b2' | 'c1';
  status: StatusDaSugestao;
  /** Dias corridos a partir do vencimento; negativo é o aviso antes de vencer. 60 na C1 é o ofício. */
  milestone: number;
  clientId: number;
  clientName: string;
  contractId: number | null;
  contractNumber: string | null;
  invoiceId: number | null;
  invoices: FaturaDaSugestao[];
  amountDue: number;
  recipients: string[];
  dueDate: string;
  daysOverdue: number;
  avisoPreVencimento?: boolean;
  /** A razão da obsolescência, na frase da fase 1. */
  blockedReason: string | null;
  discardReason?: string | null;
  /** Códigos, não frases: SEM_NOTA_AUTORIZADA, SEM_DESTINATARIO… */
  avisos: string[];
  approvedByName?: string | null;
  approvedAt?: string | null;
  oficioId?: number | null;
  oficioNumero?: string | null;
  contactId?: number | null;
  sentAt?: string | null;
  sendError?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Uma fatura que a última montagem NÃO sugeriu, com o código e a frase do servidor. */
export interface FaturaRetida {
  invoiceId: number;
  code: string;
  clientId: number;
  clientName: string;
  track: 'b2' | 'c1';
  amountDue: number;
  daysOverdue: number;
  milestone: number | null;
  codigo: string;
  motivo: string;
}

export type GrupoDeSugestao = 'b2-aviso' | 'b2-cobranca' | 'c1-email' | 'c1-oficio';

export const ORDEM_DOS_GRUPOS: GrupoDeSugestao[] = ['b2-aviso', 'b2-cobranca', 'c1-email', 'c1-oficio'];

export const GRUPO_DE_SUGESTAO: Record<GrupoDeSugestao, { trilha: string; titulo: string; sub: string; tom: 'danger' | 'warning' | 'info'; vazio: string }> = {
  'b2-aviso': {
    trilha: 'Boleto e PIX',
    titulo: 'Aviso antes do vencimento (D-3)',
    sub: 'Não é cobrança: não conta no limite de 5 nem no intervalo de 7 dias. Fica registrado em Contatos.',
    tom: 'info',
    vazio: 'Nenhum aviso para hoje.',
  },
  'b2-cobranca': {
    trilha: 'Boleto e PIX',
    titulo: 'D+3 · D+10 · D+20',
    sub: 'O texto sai pelo instrumento: com boleto vivo vai o boleto, sem boleto vai o PIX com encargos. O marco só decide quando.',
    tom: 'warning',
    vazio: 'Nenhuma cobrança de boleto ou PIX para hoje.',
  },
  'c1-email': {
    trilha: 'Órgão público e TED',
    titulo: '5 · 20 · 45 dias',
    sub: 'Lembrete ao financeiro do cliente, citando nota e empenho. Só entra fatura com nota autorizada.',
    tom: 'warning',
    vazio: 'Nenhum lembrete a órgão para hoje.',
  },
  'c1-oficio': {
    trilha: 'Órgão público e TED',
    titulo: 'Ofício aos 60 dias',
    sub: 'Um por contrato, com as faturas listadas. Aprovar cria um RASCUNHO em Ofícios; nada é enviado.',
    tom: 'danger',
    vazio: 'Nenhum ofício hoje.',
  },
};

export function grupoDaSugestao(s: Pick<SugestaoDeCobranca, 'kind' | 'track' | 'milestone'>): GrupoDeSugestao {
  if (s.kind === 'oficio') return 'c1-oficio';
  if (s.track === 'c1') return 'c1-email';
  return s.milestone < 0 ? 'b2-aviso' : 'b2-cobranca';
}

/** "D-3", "D+10", "5 dias", "ofício · 60". */
export function rotuloDoMarco(s: Pick<SugestaoDeCobranca, 'kind' | 'track' | 'milestone'>): string {
  if (s.kind === 'oficio') return `ofício · ${s.milestone}`;
  if (s.track === 'c1') return `${s.milestone} dias`;
  return s.milestone < 0 ? `D${s.milestone}` : `D+${s.milestone}`;
}

/** Quanto falta ou passou do vencimento, na fala da linha. */
export function rotuloDoAtraso(dias: number): string {
  if (dias === 0) return 'vence hoje';
  if (dias < 0) return `vence em ${-dias} ${dias === -1 ? 'dia' : 'dias'}`;
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/**
 * O1: a obsoleta fica no próprio grupo, apagada, com o motivo e só Descartar, no dia em que envelheceu. As de dias
 * anteriores ficam de fora: toda fatura paga vira uma obsoleta, e sem o recorte o grupo encheria de pagas para sempre.
 */
export function obsoletaDeHoje(s: Pick<SugestaoDeCobranca, 'status' | 'updatedAt' | 'createdAt'>, hoje = getTodayStr()): boolean {
  if (s.status !== 'obsoleta') return false;
  const quando = s.updatedAt || s.createdAt;
  return !!quando && fmtDate(quando) === fmtDate(hoje);
}

export interface GrupoMontado {
  grupo: GrupoDeSugestao;
  sugestoes: SugestaoDeCobranca[];
  /** Só as aprováveis (sugerida e falhou) somam; a obsoleta aparece mas não conta. */
  total: number;
  aprovaveis: number;
}

export const aprovavel = (s: Pick<SugestaoDeCobranca, 'status'>) => s.status === 'sugerida' || s.status === 'falhou';

/** Agrupa na ordem da prévia. Dentro do grupo: aprováveis primeiro, maior marco, vencimento mais antigo. */
export function agruparSugestoes(lista: SugestaoDeCobranca[]): GrupoMontado[] {
  return ORDEM_DOS_GRUPOS.map((grupo) => {
    const sugestoes = lista
      .filter((s) => grupoDaSugestao(s) === grupo)
      .sort((a, b) =>
        Number(aprovavel(b)) - Number(aprovavel(a))
        || b.milestone - a.milestone
        || String(a.dueDate).localeCompare(String(b.dueDate)));
    const vivas = sugestoes.filter(aprovavel);
    return { grupo, sugestoes, total: vivas.reduce((t, s) => t + (Number(s.amountDue) || 0), 0), aprovaveis: vivas.length };
  });
}

/**
 * J1 (decisão do Heitor, 30/10/2026): duas ou mais sugestões de e-mail do MESMO cliente no lote viram um aviso, e a
 * oferta é aprovar só a de maior marco hoje. As outras ficam na fila para amanhã. O ofício não entra na conta: é um
 * documento em rascunho, não um e-mail ao cliente.
 *
 * Empate de marco: a de vencimento mais antigo, depois a de maior valor.
 */
export function mesmoClienteNoLote(selecionadas: SugestaoDeCobranca[]): {
  repetidos: { clientId: number; clientName: string; sugestoes: SugestaoDeCobranca[]; fica: SugestaoDeCobranca }[];
  manter: number[];
  adiar: number[];
} {
  const porCliente = new Map<number, SugestaoDeCobranca[]>();
  for (const s of selecionadas) {
    if (s.kind !== 'email') continue;
    porCliente.set(s.clientId, [...(porCliente.get(s.clientId) || []), s]);
  }
  const repetidos: { clientId: number; clientName: string; sugestoes: SugestaoDeCobranca[]; fica: SugestaoDeCobranca }[] = [];
  const adiar = new Set<number>();
  porCliente.forEach((lista, clientId) => {
    if (lista.length < 2) return;
    const [fica, ...resto] = [...lista].sort((a, b) =>
      b.milestone - a.milestone
      || String(a.dueDate).localeCompare(String(b.dueDate))
      || (Number(b.amountDue) || 0) - (Number(a.amountDue) || 0));
    resto.forEach((s) => adiar.add(s.id));
    repetidos.push({ clientId, clientName: fica.clientName, sugestoes: lista, fica });
  });
  return { repetidos, manter: selecionadas.filter((s) => !adiar.has(s.id)).map((s) => s.id), adiar: Array.from(adiar) };
}

/** Como a faixa "Não entraram hoje" nomeia cada código do servidor. Código desconhecido aparece com o próprio nome. */
export const ROTULO_DA_RETENCAO: Record<string, string> = {
  SEM_NOTA_AUTORIZADA: 'sem nota autorizada',
  ATRASO_ALEM_DO_LIMITE: 'mais de 120 dias',
  OFICIO_RECENTE: 'ofício recente',
  TETO_DE_COBRANCAS: 'já cobrada 5 vezes',
  FORA_DE_MARCO: 'fora de marco',
  FASE1_SILENCIADA: 'pausada',
  FASE1_PROMESSA: 'promessa pendente',
  FASE1_ENTREGA: 'e-mail devolvido',
  FASE1_CADASTRO: 'cadastro',
  FASE1_ESTADO: 'estado da fatura',
  FASE1_CADENCIA: 'intervalo entre cobranças',
  FASE1_TETO: 'limite de cobranças',
};

/** Ordem da faixa: o que pede ação antes do que só espera o calendário. */
const ORDEM_DA_RETENCAO = ['SEM_NOTA_AUTORIZADA', 'FASE1_ENTREGA', 'ATRASO_ALEM_DO_LIMITE', 'TETO_DE_COBRANCAS', 'OFICIO_RECENTE'];
const posicao = (c: string) => {
  if (c === 'FORA_DE_MARCO') return 1000;
  const i = ORDEM_DA_RETENCAO.indexOf(c);
  return i < 0 ? 500 : i;
};

export interface MotivoDaRetencao {
  codigo: string;
  rotulo: string;
  faturas: FaturaRetida[];
  total: number;
  clientes: string[];
  /** A frase do servidor quando todas as faturas do código dizem o mesmo; senão a primeira. */
  motivo: string;
}

export function agruparRetidas(itens: FaturaRetida[]): MotivoDaRetencao[] {
  const por = new Map<string, FaturaRetida[]>();
  for (const f of itens) por.set(f.codigo, [...(por.get(f.codigo) || []), f]);
  return Array.from(por.entries())
    .map(([codigo, faturas]) => ({
      codigo,
      rotulo: ROTULO_DA_RETENCAO[codigo] || codigo,
      faturas,
      total: faturas.reduce((t, f) => t + (Number(f.amountDue) || 0), 0),
      clientes: Array.from(new Set(faturas.map((f) => f.clientName))),
      motivo: faturas[0]?.motivo || '',
    }))
    .sort((a, b) => posicao(a.codigo) - posicao(b.codigo));
}
